import React, { useMemo, useState } from 'react';
import { Layout, Form, Row, Col, Card, Affix, Space, Button, Input, Select, message, } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { Section } from '../../components/section/Section';
import { buildPageHeaderBreadcrumb } from '../../updated/utils';
import { Routes } from '../../routes';
import { FeatureFlag } from '../../featureFlags';
import { flags } from '../../featureFlags/flags';
import NotAuthorizedErrorView from '../NotAuthorizedErrorView';
import classNames from 'classnames';
import { useForm } from 'antd/lib/form/Form';
import { useBrandsQuery, useProfilesQuery } from '../../gql/hooks'; 
import { useMutation } from '@apollo/client';
import { CREATE_CERTIFICATE_TEMPLATE } from '../../gql';
import ApolloError from '../../components/ApolloError'; 
import { ErrorPopup } from '../../components/errorPopup/ErrorPopup';
import FileUpload from '../../components/formItems/FileUpload/FileUpload';

export const CreateCertificate = (  ) => {

    const [ form ] = useForm();
    const [ loading, setLoading ] = useState( false );

    const [ createTemplate, { error: createError } ] = useMutation( CREATE_CERTIFICATE_TEMPLATE );

    const { data: brandsData, loading: brandsLoading, error: brandsError } = useBrandsQuery( { fetchPolicy: 'network-only' } );
    const brandSelect = useMemo( () => brandsData?.brands.edges.map( edge => edge.node ) ?? [], [ brandsLoading ] );

    const { data: profilesData, loading: profilesLoading, error: profilesError } = useProfilesQuery( { fetchPolicy: 'network-only' } );
    const profilesSelect = useMemo( () => profilesData?.certificateProfiles.edges.map( edge => edge.node ) ?? [], [ profilesLoading ] );
    
    const handleFormSubmission = ( data ) => {
        setLoading( true );
        createTemplate( {
            variables: {
                input: data
            },
        } ).then( res => {
            message.success( 'Certificate Saved' );
            window.history.back();
            setLoading( false );
        } ).catch( err => {
            setLoading( false );
        } );
    };

    const handleSelectFile = ( file ) => {
        if ( !file ) {
            form.setFields( [ { name: 'file', value: undefined } ] );  
        } else {
            form.setFields( [ { name: 'file', value: file } ] );
        }
    };
    
    return <Layout className={ classNames( 'create-certificate-view', 'certificates-view' ) } >
        <FeatureFlag name={ flags.CERTIFICATES } fallback={ <NotAuthorizedErrorView /> }>
            <Layout>
                { /* Breadcrumbs */ }
        
                <PageHeader
                    title= 'Create Certificate' 
                    onBack={ () => window.history.back() }        
                    breadcrumb={ buildPageHeaderBreadcrumb( [
                        { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() },
                        { breadcrumbName: 'Incentives', path: Routes.GamificationNav.buildPath() },
                        { breadcrumbName: 'Certificates', path: Routes.ListCertificates.buildPath() },
                        { breadcrumbName: 'Create' },
                    ] ) } 
                />

                { /* Certificate Form */ }
                <Section>
                    <ErrorPopup errors={ [ profilesError, brandsError ] } />

                    { createError && <ApolloError { ...createError } /> }
                    
                    <Form   
                        name="category-form"
                        layout="vertical"
                        form={ form }
                        onFinish={ handleFormSubmission }
                        scrollToFirstError
                    >
                        <Row gutter={ [ 24, 24 ] }>
                            <Col md={ 24 }>
                                <Card title="General Settings">
                                    <Row gutter={ [ 24, 24 ] }>
                                        <Col xs={ 24 } lg={ 18 }>

                                            { /* Name input */ }
                                            
                                            <Form.Item
                                                name="name"
                                                label="Name"
                                                maxLength= { 64 }
                                                rules={ [
                                                    {
                                                        required: true,
                                                        message: 'Certificates are required to have a name.',
                                                    },
                                                    {
                                                        max: 64,
                                                        type: 'string',
                                                        message: 'Certificate names must be 64 characters or shorter.',
                                                    }
                                                ] }
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Form.Item
                                                name="description"
                                                label="Description"
                                                maxLength= { 200 }
                                                rules={ [
                                                    {
                                                        max: 200,
                                                        type: 'string',
                                                        message: 'Certificate descriptions must be 200 characters or shorter.',
                                                    }
                                                ] }
                                            >
                                                <Input.TextArea
                                                    rows={ 4 }
                                                />
                                            </Form.Item>

                                            <FeatureFlag 
                                                name={ flags.BRANDS }
                                            >
                                                <Form.Item
                                                    name='brandId'
                                                    label='Select Brand'
                                                    rules={ [
                                                        { 
                                                            required: true,
                                                            message: 'Certificates are required to have a brand.' 
                                                        }
                                                    ] }>
                                                    <Select
                                                        loading={ brandsLoading } >
                                                        { brandSelect.map( brand => <Select.Option key={ brand.id } value={ brand.id }>{ brand.name }</Select.Option> ) }
                                                    </Select>
                                                </Form.Item>
                                            </FeatureFlag>

                                            <Form.Item
                                                name="certificateProfileId"
                                                label="Profile"
                                                maxLength= { 64 }
                                                rules={ [
                                                    {
                                                        required: true,
                                                        message: 'Certificates are required to have a profile.',
                                                    }
                                                ] }
                                            >
                                                <Select
                                                    loading={ profilesLoading }
                                                >
                                                    { profilesSelect.map( prof => <Select.Option key={ prof.id } value={ prof.id }>{ prof.name }</Select.Option> ) }
                                                </Select>
                                            </Form.Item>

                                            <Form.Item
                                                name="file"
                                                label="Template"
                                                rules={ [
                                                    {
                                                        required: true,
                                                        message: 'Certificates are required to have a template.',
                                                    }
                                                ] }
                                            >
                                                
                                                <FileUpload 
                                                    accept=".pdf"
                                                    onSelectFileCallback={ handleSelectFile }
                                                /> 
                                                
                                            </Form.Item>
                                            
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </Form> 
                              
                </Section>
            </Layout>  

            { /* Right column buttons */ }
            
            <Layout.Sider className="side-action-bar" width={ 100 }>
                <Affix  offsetTop={ 98 }>
                    <Space direction="vertical" size={ 16 }>
                        
                        <React.Fragment>
                            { /* Submit button */ }
                                
                            <Button
                                type="primary"
                                htmlType="submit" 
                                loading={ loading }
                                onClick={ () => {
                                    form.submit();
                                } }
                            >
                                { loading || 'Save' }
                            </Button>
                                
                            { /* Cancel button */ }
                                
                            <Button 
                                onClick={ () => window.history.back() }
                            >
                                    Cancel
                            </Button>
                        </React.Fragment>
                        
                    </Space>
                </Affix>
            </Layout.Sider>
        </FeatureFlag>
    </Layout>;


};